<template>
    <div class="course-category-page">
        <HeaderPc />

        <div class="page-container">
            <div class="container">
                <div class="category-sidebar">
                    <div class="sidebar-header">
                        <i class="el-icon-s-grid"></i>
                        <span>课程分类</span>
                    </div>
                    <div class="category-menu">
                        <div 
                            class="menu-item"
                            v-for="(item, index) in categoryList"
                            :key="item.id"
                            :class="{ active: activeFirstIndex === index }"
                            @click="switchFirstCategory(index)"
                        >
                            <div class="menu-icon">
                                <img :src="getImageUrl(item.icon)" :alt="item.name" @error="handleIconError($event)">
                            </div>
                            <span class="menu-name">{{ item.name }}</span>
                            <i class="el-icon-arrow-right menu-arrow"></i>
                        </div>
                    </div>
                </div>

                <div class="content-area">
                    <div class="content-header">
                        <div class="breadcrumb">
                            <span class="breadcrumb-item">课程中心</span>
                            <i class="el-icon-arrow-right"></i>
                            <span class="breadcrumb-item active">{{ activeFirstCategory.name }}</span>
                            <i class="el-icon-arrow-right"></i>
                            <span class="breadcrumb-item">{{ activeSecondCategory.name }}</span>
                        </div>
                        <div class="course-count">
                            <i class="el-icon-folder-opened"></i>
                            <span>共 {{ pagination.total }} 门课程</span>
                        </div>
                    </div>

                    <div class="sub-category-bar" v-if="activeSecondList.length > 0">
                        <div class="sub-category-label">具体类目：</div>
                        <div class="sub-category-list">
                            <div 
                                class="sub-item"
                                v-for="item in activeSecondList"
                                :key="item.id"
                                :class="{ active: activeSecondId === item.id }"
                                @click="switchSecondCategory(item.id)"
                            >
                                {{ item.name }}
                            </div>
                        </div>
                    </div>

                    <div class="course-content">
                        <div class="loading-skeleton" v-if="loading">
                            <div class="skeleton-card" v-for="i in 8" :key="i">
                                <div class="skeleton-cover"></div>
                                <div class="skeleton-body">
                                    <div class="skeleton-title"></div>
                                    <div class="skeleton-text"></div>
                                </div>
                            </div>
                        </div>
                        <div class="empty-state" v-else-if="courseList.length === 0">
                            <i class="el-icon-folder-opened"></i>
                            <p>暂无相关课程</p>
                            <el-button type="text" @click="resetCategory">重置分类</el-button>
                        </div>

                        <div class="course-card-list" v-else>
                            <div class="course-card" v-for="course in courseList" :key="course.id" @click="goCourseDetail(course.id)">
                                <div class="card-header">
                                    <img :src="getImageUrl(course.cover)" alt="课程封面" class="course-cover" @error="handleCoverError($event)">
                                    <div class="course-tag" :class="course.type === 'free' ? 'free-tag' : 'pay-tag'">
                                        {{ course.is_free === 1 ? '免费' : '¥' + course.price }}
                                    </div>
                                    <div class="cover-mask">
                                        <div class="mask-content">
                                            <span><i class="el-icon-user"></i> {{ course.looks || 0 }}人</span>
                                            <span><i class="el-icon-star-on"></i> {{ course.student_count || 0 }}</span>
                                        </div>
                                    </div>
                                </div>
                                <div class="card-body">
                                    <h3 class="course-title" :title="course.name">{{ course.name }}</h3>
                                    <p class="course-teacher">
                                        <i class="el-icon-user-solid"></i>
                                        {{ course.author_name }}
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div class="pagination-wrap" v-if="courseList.length > 0">
                            <el-pagination
                                background
                                layout="prev, pager, next, total"
                                :current-page="pagination.page"
                                :page-size="pagination.pageSize"
                                :total="pagination.total"
                                @current-change="handlePageChange"
                            >
                            </el-pagination>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <FooterPc />
    </div>
</template>

<script>
import HeaderPc from '@/components/pc/header.vue';
import FooterPc from '@/components/pc/fotter.vue';
import { getCategory,getCategoryList } from '@/api/home';

export default {
    name: 'CourseCategory',
    components: {
        HeaderPc,
        FooterPc
    },
    data() {
        return {
            categoryList: [],
            activeFirstIndex: 0,
            activeSecondId: null,
            courseList: [],
            loading: false,
            pagination: {
                page: 1,
                pageSize: 12,
                total: 0
            }
        }
    },
    created() {
        this.fetchCategoryData();
    },
    watch: {
        '$route.params.id'(newId) {
            if (newId) {
                this.findAndSetActiveCategory(parseInt(newId));
            }
        }
    },
    computed: {
        activeFirstCategory() {
            return this.categoryList[this.activeFirstIndex] || {};
        },
        activeSecondList() {
            return this.activeFirstCategory.children || [];
        },
        activeSecondCategory() {
            return this.activeSecondList.find(item => item.id === this.activeSecondId) || {};
        }
    },
    methods: {
        fetchCategoryData() {
            getCategory().then(res => {
                if (res.code) {
                    this.categoryList = res.data;
                    const categoryId = this.$route.params.id;
                    if (categoryId) {
                        this.findAndSetActiveCategory(parseInt(categoryId));
                    } else {
                        this.setDefaultCategory();
                    }
                }
            }).catch(() => {
                this.$message.error('获取分类列表失败');
            });
        },
        findAndSetActiveCategory(categoryId) {
            for (let i = 0; i < this.categoryList.length; i++) {
                const firstCategory = this.categoryList[i];
                if (firstCategory.id === categoryId) {
                    this.activeFirstIndex = i;
                    this.activeSecondId = firstCategory.children?.[0]?.id || null;
                    this.fetchCourseList();
                    return;
                }
                if (firstCategory.children) {
                    const secondCategory = firstCategory.children.find(child => child.id === categoryId);
                    if (secondCategory) {
                        this.activeFirstIndex = i;
                        this.activeSecondId = categoryId;
                        this.fetchCourseList();
                        return;
                    }
                }
            }
            this.setDefaultCategory();
        },
        setDefaultCategory() {
            this.activeFirstIndex = 0;
            const firstCategory = this.categoryList[0];
            if (firstCategory) {
                this.activeSecondId = firstCategory.children?.[0]?.id || firstCategory.id;
                this.fetchCourseList();
            }
        },
        switchFirstCategory(index) {
            this.activeFirstIndex = index;
            const category = this.categoryList[index];
            this.activeSecondId = category.children?.[0]?.id || category.id;
            this.pagination.page = 1;
            this.fetchCourseList();
        },
        switchSecondCategory(id) {
            this.activeSecondId = id;
            this.pagination.page = 1;
            this.fetchCourseList();
        },
        resetCategory() {
            this.setDefaultCategory();
        },
        fetchCourseList() {
            this.loading = true;
            getCategoryList({ 
                cid: this.activeSecondId,
                page: this.pagination.page,
                limit: this.pagination.pageSize
            }).then(res => {
                if (res.code) {
                    this.courseList = res.data.list || [];
                    this.pagination.total = res.data.total || 0;
                }
            }).catch(() => {
                this.$message.error('获取课程列表失败');
            }).finally(() => {
                this.loading = false;
            });
        },
        handlePageChange(page) {
            this.pagination.page = page;
            this.fetchCourseList();
        },
        goCourseDetail(id) {
            this.$router.push({ path: `/course/${id}` });
        },
        handleCoverError(e) {
            e.target.src = require('@/static/imgs/default.jpeg');
        },
        handleIconError(e) {
            e.target.src = require('@/static/imgs/default.jpeg');
        },
        getImageUrl(url) {
            if (!url) return require('@/static/imgs/default.jpeg');
            if (typeof url === 'string' && (url.startsWith('http://') || url.startsWith('https://'))) return url;
            return process.env.VUE_APP_STATIC_URL + url;
        }
    }
};
</script>

<style scoped>
.course-category-page {
    min-height: 100vh;
    background: #f5f5f5;
}

.page-container {
    padding: 20px 0 40px;
}
.container {
    width: 80%;
    margin: 0 auto;
    display: flex;
    gap: 20px;
}

.category-sidebar {
    width: 220px;
    flex-shrink: 0;
}
.sidebar-header {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
    padding: 16px;
    border-radius: 8px 8px 0 0;
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 16px;
    font-weight: 600;
}
.sidebar-header i {
    font-size: 18px;
}
.category-menu {
    background: #fff;
    border-radius: 0 0 8px 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.08);
    overflow: hidden;
}
.menu-item {
    display: flex;
    align-items: center;
    padding: 14px 16px;
    cursor: pointer;
    transition: all 0.3s ease;
    border-bottom: 1px solid #f5f5f5;
    position: relative;
}
.menu-item:last-child {
    border-bottom: none;
}
.menu-item::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 0;
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    transition: width 0.3s ease;
}
.menu-item:hover {
    background: #f8f9ff;
}
.menu-item:hover::before {
    width: 4px;
}
.menu-item.active {
    background: linear-gradient(90deg, #f0f2ff 0%, #fff 100%);
    color: #667eea;
}
.menu-item.active::before {
    width: 4px;
}
.menu-icon {
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #f5f7fa;
    border-radius: 6px;
    margin-right: 12px;
    transition: all 0.3s ease;
    overflow: hidden;
}
.menu-item.active .menu-icon {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}
.menu-icon img {
    width: 20px;
    height: 20px;
    object-fit: contain;
}
.menu-name {
    flex: 1;
    font-size: 14px;
}
.menu-arrow {
    font-size: 12px;
    color: #d0d0d0;
    transition: all 0.3s ease;
}
.menu-item:hover .menu-arrow,
.menu-item.active .menu-arrow {
    color: #667eea;
    transform: translateX(3px);
}

.content-area {
    flex: 1;
    min-width: 0;
}
.content-header {
    background: #fff;
    padding: 16px 20px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    justify-content: space-between;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.breadcrumb {
    display: flex;
    align-items: center;
    gap: 8px;
    font-size: 14px;
    color: #666;
}
.breadcrumb i {
    font-size: 12px;
    color: #d0d0d0;
}
.breadcrumb-item.active {
    color: #667eea;
    font-weight: 600;
}
.course-count {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #667eea;
    font-size: 14px;
    background: #f0f2ff;
    padding: 6px 14px;
    border-radius: 16px;
}

.sub-category-bar {
    background: #fff;
    padding: 16px 20px;
    border-radius: 8px;
    margin-bottom: 16px;
    display: flex;
    align-items: center;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.sub-category-label {
    font-size: 14px;
    color: #666;
    margin-right: 16px;
    flex-shrink: 0;
}
.sub-category-list {
    display: flex;
    flex-wrap: wrap;
    gap: 10px;
}
.sub-item {
    padding: 6px 18px;
    border-radius: 16px;
    font-size: 13px;
    color: #666;
    background: #f5f7fa;
    cursor: pointer;
    transition: all 0.3s ease;
}
.sub-item:hover {
    background: #e8ebff;
    color: #667eea;
}
.sub-item.active {
    background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
    color: #fff;
}

.course-content {
    background: #fff;
    padding: 20px;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.05);
}
.empty-state {
    text-align: center;
    padding: 80px 0;
    color: #94a3b8;
}
.empty-state i {
    font-size: 64px;
    margin-bottom: 16px;
    color: #cbd5e1;
}
.empty-state p {
    font-size: 16px;
    margin: 0 0 20px 0;
}

.course-card-list {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
}
.course-card {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #f0f0f0;
    cursor: pointer;
    transition: all 0.3s ease;
}
.course-card:hover {
    transform: translateY(-4px);
    box-shadow: 0 8px 20px rgba(102,126,234,0.15);
    border-color: #667eea;
}
.card-header {
    position: relative;
    height: 140px;
    overflow: hidden;
}
.course-cover {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.3s ease;
}
.course-card:hover .course-cover {
    transform: scale(1.05);
}
.course-tag {
    position: absolute;
    top: 8px;
    right: 8px;
    padding: 4px 10px;
    border-radius: 4px;
    font-size: 12px;
    font-weight: 600;
    color: #fff;
}
.free-tag {
    background: linear-gradient(135deg, #11998e 0%, #38ef7d 100%);
}
.pay-tag {
    background: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
}
.cover-mask {
    position: absolute;
    bottom: 0;
    left: 0;
    right: 0;
    background: linear-gradient(to top, rgba(0,0,0,0.7), transparent);
    padding: 10px;
    opacity: 0;
    transition: opacity 0.3s ease;
}
.course-card:hover .cover-mask {
    opacity: 1;
}
.mask-content {
    display: flex;
    justify-content: space-between;
    color: #fff;
    font-size: 11px;
}
.mask-content span {
    display: flex;
    align-items: center;
    gap: 4px;
}

.card-body {
    padding: 12px;
}
.course-title {
    font-size: 14px;
    font-weight: 500;
    color: #333;
    margin: 0 0 8px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}
.course-teacher {
    font-size: 12px;
    color: #999;
    display: flex;
    align-items: center;
    gap: 4px;
    margin: 0;
}
.course-teacher i {
    font-size: 11px;
}

.pagination-wrap {
    margin-top: 30px;
    display: flex;
    justify-content: center;
}

.loading-skeleton {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
    gap: 20px;
}
.skeleton-card {
    background: #fff;
    border-radius: 8px;
    overflow: hidden;
    border: 1px solid #f0f0f0;
}
.skeleton-cover {
    width: 100%;
    height: 140px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
}
.skeleton-body {
    padding: 12px;
}
.skeleton-title {
    height: 16px;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;
    margin-bottom: 8px;
}
.skeleton-text {
    height: 12px;
    width: 60%;
    background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
    background-size: 200% 100%;
    animation: skeleton-loading 1.5s infinite;
    border-radius: 4px;
}
@keyframes skeleton-loading {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}
</style>
